import * as React from 'react';
import Link from 'next/link';
import classNames from 'styles/utils/classNames';
import { PRICING_PAGE } from 'constants/pages';
import type { MarketSessionPhase } from 'utils/shared/time/marketSession';
import {
  CTA_LABEL,
  FEED_ROWS,
  FLOW_ROW_FEED,
  GAMMA_HERO_BODY,
  GAMMA_HERO_HEADLINE,
  isFeedOpen,
  leadLine,
  LIVE_TAPE_LINE,
  DELAYED_CHIP,
  SESSION_CHIP,
  LOCKED_CHIP,
  OPEN_INTEREST_CHIP,
  OPEN_INTEREST_LINE,
  OPEN_INTEREST_ROW_FEED,
  OPEN_INTEREST_WAITING_CHIP,
  formatFeedWindow,
  openInterestScheduleLabel,
  openInterestRowState,
  PANEL_TITLE,
  zoneLabel,
  upgradeLine,
  type FlowAccess,
  type MarketDataSurface,
} from './marketDataStatusModel';
import { SESSION_TONES } from './sessionTones';
import StatusDot from './StatusDot';

export interface MarketDataPanelProps {
  surface: MarketDataSurface;
  phase: MarketSessionPhase;
  flowAccess: FlowAccess;
  /** Weekday of the next relevant open, or null when it is later the same day. */
  weekday: string | null;
  /** False before the clock has been read, so no row may claim to be delivering. */
  resolved: boolean;
  /** IANA zone the viewer reads the app in; windows are converted into it. */
  zone: string;
  /** Reference instant for the conversion, so DST is handled for the session on screen. */
  nowMs: number;
  /**
   * The ET session of the open interest on screen (GEX surface only). The Open Interest row reads
   * "Today" when it is today's date and a session is running; null or missing leaves the row dim.
   */
  oiSessionDate?: string | null;
}

interface FeedLineProps {
  feed: string;
  /** Delivering now: a session-coloured dot and a bright label. Otherwise the whole row is dim. */
  open: boolean;
  phase: MarketSessionPhase;
  /** The one chip in the chip column, or nothing. */
  chip: React.ReactNode;
  /** The right-hand column: a window, or the time a once-a-day feed is due. */
  time: string;
}

/** One row of the panel. Every row, including Open Interest, uses this markup so they align. */
function FeedLine({ feed, open, phase, chip, time }: FeedLineProps) {
  return (
    <div
      data-feed={feed}
      data-feed-open={open ? 'true' : 'false'}
      className="flex items-center justify-between gap-3 py-[7px]"
    >
      <span className="flex min-w-0 items-center gap-2">
        <StatusDot phase={open ? phase : 'closed'} live={false} size="sm" />
        <span
          className={classNames(
            'whitespace-nowrap text-[12px] font-medium',
            open ? 'text-white' : 'text-palette-black-6',
          )}
        >
          {feed}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {/*
          ONE CHIP PER ROW, IN ONE COLUMN.

          The tier badge sits in the same slot as the session chip rather than beside the
          label. Stacking both against a feed name is what truncated "Options Flow" to
          "Options…" — and on the one row that carries the upgrade, the badge is the more
          useful of the two: a delayed viewer already knows the tape is open, and what they
          need to see is that theirs is behind.
        */}
        {chip}
        <span
          className={classNames(
            'w-[124px] whitespace-nowrap text-right font-mono text-[10.5px] tabular-nums',
            open ? 'text-palette-black-8' : 'text-palette-black-6',
          )}
        >
          {time}
        </span>
      </span>
    </div>
  );
}

interface SessionChipProps {
  phase: MarketSessionPhase;
  children: string;
}

/** The session-coloured chip on a delivering row. */
function SessionChip({ phase, children }: SessionChipProps) {
  return (
    <span
      className={classNames(
        'whitespace-nowrap rounded px-1.5 py-[3px] text-[8.5px] font-bold uppercase leading-none tracking-[0.1em]',
        SESSION_TONES[phase].chip,
      )}
    >
      {children}
    </span>
  );
}

/**
 * The panel body — pure, props-driven, and deliberately free of clocks, portals and state.
 *
 * Everything worth asserting about this control lives here, which is what lets the whole content
 * contract be tested with `renderToStaticMarkup` in a plain Node environment instead of standing up
 * a DOM. The interactive shell around it stays thin enough to verify in a browser.
 */
export default function MarketDataPanel({
  surface,
  phase,
  flowAccess,
  weekday,
  resolved,
  zone,
  nowMs,
  oiSessionDate = null,
}: MarketDataPanelProps) {
  const tone = SESSION_TONES[phase];
  const showsDelay = flowAccess === 'delayed';
  const showsLiveTape = flowAccess === 'live';
  // "Today" uses the same rule as the dot beside the OI totals on the page (`isTodaysOpenInterest`).
  const openInterestState = resolved ? openInterestRowState(oiSessionDate, phase, nowMs) : null;

  return (
    <div
      data-market-data-panel={surface}
      className="w-[348px] max-w-[calc(100vw-20px)] overflow-hidden rounded-xl border border-white/[0.11] bg-palette-black-2 shadow-[0_28px_70px_-16px_rgba(0,0,0,0.9)]"
    >
      <div className={classNames('bg-gradient-to-br to-transparent px-4 pb-3.5 pt-3.5', tone.wash)}>
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="font-space text-[15px] font-bold leading-tight text-white">
            {PANEL_TITLE}
          </h2>
          <span className="shrink-0 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-palette-black-6">
            {zoneLabel(zone)}
          </span>
        </div>
        <p className="mt-1.5 text-[11.5px] leading-[1.5] text-text-secondary">
          {leadLine(phase, weekday, zone, nowMs)}
        </p>
      </div>

      <div className="border-t border-white/[0.07] px-4 py-1.5">
        {FEED_ROWS.map((row) => {
          const rowOpen = resolved && isFeedOpen(row, phase);
          const isFlowRow = row.feed === FLOW_ROW_FEED;
          /*
            ONE CHIP PER ROW, NAMING WHAT IT DELIVERS.

            A running row shows the session it is delivering in: `Live` in the regular hours, or
            `Pre-market` / `After-hours` in the extended sessions, where prices still move but on
            thinner, wider prints than the cash tape. `Delayed` is the tape a free viewer gets 30
            minutes late; `Locked` is dark pool, which they do not get at all. One chip per row,
            because two vocabularies on one clock is the thing that makes people write in.

            The tier chip is a statement about a feed that is DELIVERING, so it is gated on the same
            `rowOpen` as the session chip it replaces. Outside the feed's own window there is nothing
            to qualify: an amber "Delayed" beside a grey, closed dot claims a tape is arriving-but-
            late when it is not arriving at all. When the row is dark, so is its chip.
          */
          const locked = rowOpen && showsDelay && !!row.subscriberOnly;
          const delayed = rowOpen && showsDelay && isFlowRow;

          return (
            <FeedLine
              key={row.feed}
              feed={row.feed}
              open={rowOpen}
              phase={phase}
              chip={
                locked || delayed ? (
                  <span
                    data-tier-chip={locked ? 'locked' : 'delayed'}
                    className="whitespace-nowrap rounded bg-amber-400/15 px-1.5 py-[3px] text-[8.5px] font-bold uppercase leading-none tracking-[0.1em] text-amber-300"
                  >
                    {locked ? LOCKED_CHIP : DELAYED_CHIP}
                  </span>
                ) : (
                  rowOpen && <SessionChip phase={phase}>{SESSION_CHIP[phase]}</SessionChip>
                )
              }
              time={formatFeedWindow(row, zone, nowMs)}
            />
          );
        })}
        {surface === 'gex' && (
          // Open interest is a once-a-day file, so its time column states the daily schedule rather
          // than a window, and its chip says whether today's is on screen yet. "Waiting" keeps the
          // row dim, with the grey chip of a feed that is not delivering.
          <FeedLine
            feed={OPEN_INTEREST_ROW_FEED}
            open={openInterestState === 'today'}
            phase={phase}
            chip={
              openInterestState === 'today' ? (
                <SessionChip phase={phase}>{OPEN_INTEREST_CHIP}</SessionChip>
              ) : (
                openInterestState === 'waiting' && (
                  <SessionChip phase="closed">{OPEN_INTEREST_WAITING_CHIP}</SessionChip>
                )
              )
            }
            time={openInterestScheduleLabel(zone, nowMs)}
          />
        )}
      </div>

      {surface === 'gex' && (
        <div className="border-t border-white/[0.07] bg-gradient-to-br from-bullish-green/[0.08] via-transparent to-transparent px-4 py-3">
          <p className="font-space text-[13px] font-bold leading-tight text-white">
            {GAMMA_HERO_HEADLINE}
          </p>
          <p className="mt-1.5 text-[11.5px] leading-[1.5] text-text-secondary">
            {GAMMA_HERO_BODY}
          </p>
          <p className="mt-2.5 border-t border-white/[0.06] pt-2.5 text-[11px] leading-[1.5] text-palette-black-7">
            {OPEN_INTEREST_LINE}
          </p>
        </div>
      )}

      {showsDelay && (
        <div className="border-t border-white/[0.07] bg-palette-black-1/70 px-4 py-3">
          <p className="text-[11.5px] leading-[1.5] text-text-secondary">{upgradeLine(surface)}</p>
          <Link
            href={PRICING_PAGE}
            className="mt-2.5 inline-flex items-center gap-1.5 rounded-md bg-bullish-green px-2.5 py-1.5 text-[11px] font-bold leading-none text-palette-black-1 transition-colors hover:bg-bullish-green/85 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          >
            {CTA_LABEL}
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      )}

      {showsLiveTape && (
        <p className="border-t border-white/[0.07] bg-palette-black-1/70 px-4 py-2.5 text-[11.5px] leading-[1.5] text-text-secondary">
          {LIVE_TAPE_LINE}
        </p>
      )}
    </div>
  );
}
